import type { ReactElement } from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MarkdownRemarkFrontmatterAdvantagesCards, Maybe } from '../../../generated/types';
import type { PpMarkdownImageWithAlt } from '../../images/types';
import { InquiryButton } from '../../components/buttons/InquiryButton';
import { TextWithNoahLogo } from '../../components/typography/TextWithNoahLogo';
import { HomepageCardWithImage } from './HomepageCardWithImage';

type PpAdvantagesSection = {
  buttonLabel?: Maybe<string> | undefined;
  cards: MarkdownRemarkFrontmatterAdvantagesCards[];
  image: PpMarkdownImageWithAlt | undefined;
  textWithIconLabel: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: theme.breakpoints.values.lg,
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 3),
  },
}));

const StyledImage = styled('img')(({ theme }) => ({
  filter: 'drop-shadow(0px 24px 40px rgba(0, 0, 0, 0.1))',
  padding: theme.spacing(5),
  width: theme.breakpoints.values.lg,
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(5),
    padding: 0,
    width: '100%',
  },
}));

export function AdvantagesSection({
  buttonLabel,
  cards,
  image,
  textWithIconLabel,
  title,
}: PpAdvantagesSection): ReactElement {
  return (
    <Box component="section" overflow="hidden" width="100%">
      <StyledContainer disableGutters fixed>
        <Stack>
          <TextWithNoahLogo>{textWithIconLabel}</TextWithNoahLogo>
          <Typography variant="headingL">{title}</Typography>
          <Grid container>
            <Grid size={{ lg: 6, xs: 12 }}>
              <Stack alignItems="center" height="100%" justifyContent="center" spacing={2.5}>
                {cards.map((card) => (
                  <HomepageCardWithImage key={card.id} darkText directionFrom="left" {...card} />
                ))}
                {buttonLabel && (
                  <InquiryButton color="info" variant="outlined">
                    {buttonLabel}
                  </InquiryButton>
                )}
              </Stack>
            </Grid>
            <Grid size={{ lg: 6, xs: 12 }}>
              {image?.src && image?.alt && <StyledImage alt={image?.alt} src={image?.src} />}
            </Grid>
          </Grid>
        </Stack>
      </StyledContainer>
    </Box>
  );
}
